import { onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { backButton, haptic } from '@/composables/useTelegram'

/** С какого края экрана начинается жест возврата, px. */
const EDGE = 30

/** Насколько нужно провести вправо, чтобы это считалось возвратом, px. */
const DISTANCE = 70

/**
 * Возврат назад по-человечески: кнопка «назад» в шапке Telegram и жест
 * от левого края.
 *
 * Без этого из мини-аппа было три выхода и ни одного возврата: системная
 * кнопка «назад» на Android и свайп закрывали приложение целиком, хотя клиент
 * всего лишь хотел вернуться из корзины в меню. Пока кнопка показана, Telegram
 * шлёт эти нажатия нам, а не закрывает окно.
 *
 * Кнопку показываем, только когда внутри приложения действительно есть куда
 * возвращаться: на первом экране после запуска она бы уводила из мини-аппа
 * вслепую. Глубину берём из истории роутера — он нумерует записи сам.
 */
export function useBackNavigation(): void {
  const router = useRouter()

  /** Номер записи истории на старте — всё, что выше, пройдено внутри приложения. */
  const start = position()

  const canGoBack = ref(false)

  function position(): number {
    return Number(window.history.state?.position ?? 0)
  }

  function sync(): void {
    canGoBack.value = position() > start
  }

  function goBack(): void {
    if (!canGoBack.value) return

    haptic.impact('light')
    router.back()
  }

  const stopAfterEach = router.afterEach(sync)
  sync()

  watch(canGoBack, (value) => (value ? backButton.show() : backButton.hide()), {
    immediate: true,
  })

  backButton.onClick(goBack)

  /*
   * Жест возврата: ведём пальцем от левого края вправо. Начало у самого края —
   * иначе жест воевал бы с горизонтальными лентами (баннеры, категории,
   * «часто покупают»), которые листаются тем же движением.
   */
  let startX = 0
  let startY = 0
  let tracking = false

  function onStart(event: TouchEvent): void {
    const touch = event.touches[0]
    if (!touch) return

    tracking = touch.clientX <= EDGE
    startX = touch.clientX
    startY = touch.clientY
  }

  function onEnd(event: TouchEvent): void {
    if (!tracking) return
    tracking = false

    const touch = event.changedTouches[0]
    if (!touch) return

    const dx = touch.clientX - startX
    const dy = Math.abs(touch.clientY - startY)

    /* Вертикальную часть жеста отсекаем: это скролл, а не возврат */
    if (dx >= DISTANCE && dx > dy * 2) goBack()
  }

  document.addEventListener('touchstart', onStart, { passive: true })
  document.addEventListener('touchend', onEnd, { passive: true })

  onBeforeUnmount(() => {
    stopAfterEach()
    backButton.offClick(goBack)
    backButton.hide()
    document.removeEventListener('touchstart', onStart)
    document.removeEventListener('touchend', onEnd)
  })
}
